import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  OPERATING_SYSTEM_PREVIEW_LOADED_EVENT,
  OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT,
  useOperatingSystemPreviewCache,
  type OperatingSystemPreviewLoadedDetail,
  type OperatingSystemPreviewProviderLoadingDetail,
} from "@/components/operating-systems/OperatingSystemPreviewCacheContext";
import { ToastAction } from "@/components/ui/toast";
import { useAuth } from "@/components/auth/AuthContext";
import { shouldPauseAppPolling } from "@/components/auth/authRecoveryPolling";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  completedAppJobNotification,
  completedAppJobRequiresRuntime,
  shouldNotifyCompletedAppJob,
} from "@/lib/app-job-notifications";
import { playErrorSound, playSuccessSound } from "@/lib/errorSound";
import {
  OPERATING_SYSTEM_JOB_SAFE_SELECT,
  OPERATING_SYSTEM_RUNTIME_CACHE_SAFE_SELECT,
  OPERATING_SYSTEM_RUNTIME_LIFECYCLE_SAFE_SELECT,
  OperatingSystemApp,
  OperatingSystemJob,
  OperatingSystemPreviewDescriptor,
  OperatingSystemProxyTicket,
  OperatingSystemRuntimeCache,
  OperatingSystemRuntimeLifecycle,
  getOperatingSystemDetailPath,
  getOperatingSystemFeedbackLabel,
  getOperatingSystemPreviewDescriptor,
  getOperatingSystemStatusLabel,
  hasOperatingSystemRunnableRuntime,
  isActiveOsJobStatus,
} from "@/lib/operating-systems";

const STORAGE_KEY = "applyt_os_notified_jobs";
const READY_NOTIFICATION_TIMEOUT_MS = 8 * 60 * 1000;
const READY_NOTIFICATION_PREPARE_RETRY_MS = 5000;
const READY_NOTIFICATION_REFRESH_MIN_INTERVAL_MS = 8000;
const READY_NOTIFICATION_MAX_REFRESH_ATTEMPTS = 20;

function loadNotifiedJobs(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    return new Set(Array.isArray(parsed) ? parsed.filter((item) => typeof item === "string") : []);
  } catch {
    return new Set();
  }
}

function saveNotifiedJobs(ids: Set<string>) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(ids).slice(-80)));
}

export function OperatingSystemJobNotifier() {
  const { user, isAnonymous, authRecoveryState } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    isPreviewLoadedNow,
    prewarmPreview,
    reloadPreview,
  } = useOperatingSystemPreviewCache();
  const initializedRef = useRef(false);
  const previousStatusRef = useRef<Record<string, string>>({});
  const notifiedJobsRef = useRef<Set<string>>(loadNotifiedJobs());
  const pendingReadyJobsRef = useRef<Set<string>>(new Set());

  const requestRuntimeTicket = useCallback(async (operatingSystemId: string): Promise<OperatingSystemProxyTicket | null> => {
    try {
      const { data, error } = await supabase.functions.invoke("os-runtime-ticket", {
        body: { operatingSystemId },
      });
      if (error) throw error;
      const proxyUrl = typeof data?.proxyUrl === "string" ? data.proxyUrl : "";
      const expiresAt = typeof data?.expiresAt === "string" ? data.expiresAt : "";
      return proxyUrl && expiresAt ? { proxyUrl, expiresAt } : null;
    } catch (error) {
      console.warn("Failed to prepare App runtime notification ticket:", error);
      return null;
    }
  }, []);

  const loadRuntimePreviewDescriptor = useCallback(async (
    app: Pick<OperatingSystemApp, "id" | "name" | "status" | "ready_at" | "version">,
  ): Promise<OperatingSystemPreviewDescriptor | null> => {
    try {
      const { data: cacheRow, error: cacheError } = await supabase
        .from("operating_system_runtime_cache")
        .select(OPERATING_SYSTEM_RUNTIME_CACHE_SAFE_SELECT)
        .eq("operating_system_id", app.id)
        .maybeSingle();
      if (cacheError) throw cacheError;

      const runtimeCache = (cacheRow || null) as unknown as OperatingSystemRuntimeCache | null;

      const { data: lifecycleRow, error: lifecycleError } = await supabase
        .from("operating_system_runtime_lifecycle")
        .select(OPERATING_SYSTEM_RUNTIME_LIFECYCLE_SAFE_SELECT)
        .eq("operating_system_id", app.id)
        .maybeSingle();
      if (lifecycleError) throw lifecycleError;

      const runtimeLifecycle = (lifecycleRow || null) as unknown as OperatingSystemRuntimeLifecycle | null;
      if (!hasOperatingSystemRunnableRuntime(app, runtimeCache, runtimeLifecycle)) return null;

      const ticket = await requestRuntimeTicket(app.id);
      if (!ticket) return null;

      return getOperatingSystemPreviewDescriptor(app as OperatingSystemApp, runtimeCache, ticket);
    } catch (error) {
      console.warn("Failed to prepare App runtime notification preview:", error);
      return null;
    }
  }, [requestRuntimeTicket]);

  const prepareRuntimePreviewDescriptor = useCallback(async (
    app: Pick<OperatingSystemApp, "id" | "name" | "status" | "ready_at" | "version">,
  ): Promise<OperatingSystemPreviewDescriptor | null> => {
    const startedAt = Date.now();
    while (Date.now() - startedAt < READY_NOTIFICATION_TIMEOUT_MS) {
      const descriptor = await loadRuntimePreviewDescriptor(app);
      if (descriptor) return descriptor;
      await new Promise((resolve) => window.setTimeout(resolve, READY_NOTIFICATION_PREPARE_RETRY_MS));
    }
    return null;
  }, [loadRuntimePreviewDescriptor]);

  const waitForRuntimePreviewLoaded = useCallback((
    app: Pick<OperatingSystemApp, "id" | "name" | "status" | "ready_at" | "version">,
    initialDescriptor: OperatingSystemPreviewDescriptor,
  ): Promise<boolean> => new Promise((resolve) => {
    let currentDescriptor = initialDescriptor;
    let finished = false;
    let refreshInFlight = false;
    let refreshAttempts = 0;
    let refreshLastAt = 0;
    let pollTimer: number | null = null;
    let timeoutTimer: number | null = null;

    const finish = (loaded: boolean) => {
      if (finished) return;
      finished = true;
      window.removeEventListener(OPERATING_SYSTEM_PREVIEW_LOADED_EVENT, handleLoaded);
      window.removeEventListener(OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT, handleProviderLoading);
      if (pollTimer !== null) window.clearInterval(pollTimer);
      if (timeoutTimer !== null) window.clearTimeout(timeoutTimer);
      resolve(loaded);
    };

    const checkLoaded = () => {
      if (isPreviewLoadedNow(currentDescriptor)) finish(true);
    };

    const refreshRuntimePreview = async () => {
      if (finished || refreshInFlight) return;
      const now = Date.now();
      if (now - refreshLastAt < READY_NOTIFICATION_REFRESH_MIN_INTERVAL_MS) return;
      if (refreshAttempts >= READY_NOTIFICATION_MAX_REFRESH_ATTEMPTS) return;

      refreshInFlight = true;
      refreshLastAt = now;
      refreshAttempts += 1;
      try {
        await supabase.functions.invoke("refresh-os-preview", {
          body: { operatingSystemId: app.id },
        });
        const nextDescriptor = await loadRuntimePreviewDescriptor(app);
        if (!nextDescriptor) return;
        currentDescriptor = nextDescriptor;
        reloadPreview(currentDescriptor);
        checkLoaded();
      } catch (error) {
        console.warn("Failed to refresh App runtime before ready notification:", error);
      } finally {
        refreshInFlight = false;
      }
    };

    function handleLoaded(event: Event) {
      const detail = (event as CustomEvent<OperatingSystemPreviewLoadedDetail>).detail;
      if (detail?.osId === app.id && detail.cacheKey === currentDescriptor.cacheKey) {
        finish(true);
      }
    }

    function handleProviderLoading(event: Event) {
      const detail = (event as CustomEvent<OperatingSystemPreviewProviderLoadingDetail>).detail;
      if (detail?.osId === app.id && detail.cacheKey === currentDescriptor.cacheKey) {
        void refreshRuntimePreview();
      }
    }

    window.addEventListener(OPERATING_SYSTEM_PREVIEW_LOADED_EVENT, handleLoaded);
    window.addEventListener(OPERATING_SYSTEM_PREVIEW_PROVIDER_LOADING_EVENT, handleProviderLoading);
    prewarmPreview(currentDescriptor);
    checkLoaded();
    pollTimer = window.setInterval(checkLoaded, 1000);
    timeoutTimer = window.setTimeout(() => finish(false), READY_NOTIFICATION_TIMEOUT_MS);
  }), [isPreviewLoadedNow, loadRuntimePreviewDescriptor, prewarmPreview, reloadPreview]);

  const notifyCompletedJob = useCallback((
    job: Pick<OperatingSystemJob, "id" | "job_type">,
    app: Pick<OperatingSystemApp, "id" | "name" | "status" | "ready_at" | "version">,
  ) => {
    if (notifiedJobsRef.current.has(job.id)) return;
    notifiedJobsRef.current.add(job.id);
    saveNotifiedJobs(notifiedJobsRef.current);
    playSuccessSound();
    const notification = completedAppJobNotification(job.job_type);
    toast({
      title: notification.title,
      description: app.name,
      action: (
        <ToastAction
          altText={notification.actionLabel}
          onClick={() => {
            if (notification.destination === "templates") {
              navigate("/templates");
              return;
            }
            navigate(getOperatingSystemDetailPath(app.id));
          }}
        >
          {notification.actionLabel}
        </ToastAction>
      ),
    });
  }, [navigate, toast]);

  const notifyCompletedJobWhenRuntimeLoaded = useCallback(async (
    job: Pick<OperatingSystemJob, "id" | "job_type">,
    app: Pick<OperatingSystemApp, "id" | "name" | "status" | "ready_at" | "version">,
  ) => {
    if (pendingReadyJobsRef.current.has(job.id) || notifiedJobsRef.current.has(job.id)) return;
    pendingReadyJobsRef.current.add(job.id);

    try {
      const descriptor = await prepareRuntimePreviewDescriptor(app);
      if (!descriptor) return;

      const loaded = await waitForRuntimePreviewLoaded(app, descriptor);
      if (!loaded) return;
      notifyCompletedJob(job, app);
    } finally {
      pendingReadyJobsRef.current.delete(job.id);
    }
  }, [notifyCompletedJob, prepareRuntimePreviewDescriptor, waitForRuntimePreviewLoaded]);

  const pollJobs = useCallback(async () => {
    if (!user?.id || isAnonymous || shouldPauseAppPolling(authRecoveryState)) return;

    const { data: appRows, error: appError } = await supabase
      .from("operating_system_apps")
      .select("id, name, status, latest_job_id, failure_reason, latest_job_feedback, version, ready_at, updated_at")
      .eq("user_id", user.id)
      .neq("status", "archived")
      .order("updated_at", { ascending: false })
      .limit(50);

    if (appError) {
      console.warn("Failed to poll OS apps:", appError);
      return;
    }

    const apps = (appRows || []) as Pick<
      OperatingSystemApp,
      "id" | "name" | "status" | "latest_job_id" | "failure_reason" | "latest_job_feedback" | "version" | "ready_at" | "updated_at"
    >[];
    const appIds = apps.map((app) => app.id);
    if (appIds.length === 0) {
      initializedRef.current = true;
      return;
    }

    const { data: jobRows, error: jobError } = await supabase
      .from("operating_system_jobs")
      .select(OPERATING_SYSTEM_JOB_SAFE_SELECT)
      .in("operating_system_id", appIds)
      .order("updated_at", { ascending: false })
      .limit(100);

    if (jobError) {
      console.warn("Failed to poll App jobs:", jobError);
      return;
    }

    const jobs = (jobRows || []) as unknown as Pick<
      OperatingSystemJob,
      "id" | "operating_system_id" | "status" | "job_type" | "progress_percent" | "current_step" | "error_message" | "updated_at"
    >[];
    const appById = new Map(apps.map((app) => [app.id, app]));
    const nextStatuses: Record<string, string> = {};

    for (const job of jobs) {
      nextStatuses[job.id] = job.status;
      if (!initializedRef.current) continue;

      const previous = previousStatusRef.current[job.id];
      const wasActive = isActiveOsJobStatus(previous);
      const isTerminal = job.status === "completed" || job.status === "failed";
      const alreadyNotified = notifiedJobsRef.current.has(job.id);
      if (!wasActive || !isTerminal || alreadyNotified || !shouldNotifyCompletedAppJob(job)) continue;

      const app = appById.get(job.operating_system_id);
      if (!app) continue;

      if (job.status === "completed") {
        if (completedAppJobRequiresRuntime(job.job_type)) {
          void notifyCompletedJobWhenRuntimeLoaded(job, app);
        } else {
          notifyCompletedJob(job, app);
        }
      } else {
        notifiedJobsRef.current.add(job.id);
        saveNotifiedJobs(notifiedJobsRef.current);
        const feedback = getOperatingSystemFeedbackLabel(
          job.error_message || app.failure_reason,
        );
        playErrorSound();
        toast({
          title: `${getOperatingSystemStatusLabel(app.status)}: ${app.name}`,
          description: feedback || "The App job could not finish.",
          variant: "destructive",
          action: (
            <ToastAction altText="View details" onClick={() => navigate("/applications")}>
              View details
            </ToastAction>
          ),
        });
      }
    }

    previousStatusRef.current = nextStatuses;
    initializedRef.current = true;
  }, [authRecoveryState, isAnonymous, navigate, notifyCompletedJob, notifyCompletedJobWhenRuntimeLoaded, toast, user?.id]);

  useEffect(() => {
    void pollJobs();
    const interval = window.setInterval(() => {
      void pollJobs();
    }, 15000);
    return () => window.clearInterval(interval);
  }, [pollJobs]);

  return null;
}
